import { useEffect } from 'react';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { useCards } from '../../store/cards';
import { stopAlarm, useTimer, timeLeft } from '../../store/timer';
import { useUi } from '../../store/ui';
import controls from '../controls.module.css';
import { Cross, Timer } from '../icons';
import styles from './TimerBar.module.css';

/** The running ten minutes, or the "time is up" offer, on one line under the top bar. */
export function TimerBar() {
  const t = useT();
  const running = useTimer((s) => s.running);
  const askFor = useTimer((s) => s.askFor);
  const stop = useTimer((s) => s.stop);
  const answer = useTimer((s) => s.answer);
  const ensure = useTimer((s) => s.ensure);
  const openSheet = useUi((s) => s.openSheet);
  const cardId = running?.cardId ?? askFor;
  const card = useCards((s) => (cardId ? s.cards[cardId] : undefined));
  useEffect(() => ensure(), [ensure]);
  if (!card || card.deletedAt !== null) return null;
  return (
    <div className={`${styles.bar}${running ? '' : ` ${styles.up}`}`} role="status" data-timer={running ? 'running' : 'up'}>
      <Timer size={18} />
      <button type="button" className={styles.title} onClick={() => openSheet(card.id)}>
        {running ? card.title : t.t('timer.up', { title: card.title })}
      </button>
      {running ? <Countdown running={running} /> : null}
      {running ? (
        <button type="button" className={`${controls.btn} ${controls.quiet} ${styles.action}`} onClick={stop}>
          {t.t('timer.stop')}
        </button>
      ) : (
        <>
          <button
            type="button"
            className={`${controls.btn} ${controls.primary} ${styles.action}`}
            onClick={() => {
              stopAlarm();
              openSheet(card.id);
            }}
          >
            {t.t('timer.open')}
          </button>
          <button type="button" className={controls.iconbtn} onClick={answer} aria-label={t.t('timer.dismiss')} title={t.t('timer.dismiss')}>
            <Cross size={18} />
          </button>
        </>
      )}
    </div>
  );
}

function Countdown({ running }: { running: { cardId: string; startedAt: number; endsAt: number } }) {
  const t = useT();
  const now = useNow(1000);
  const ensure = useTimer((s) => s.ensure);
  useEffect(() => {
    if (now >= running.endsAt) ensure();
  }, [now, running.endsAt, ensure]);
  return (
    <span className={styles.left} aria-label={t.t('timer.left', { time: timeLeft(running, now) })}>
      {timeLeft(running, now)}
    </span>
  );
}
